import axios from "axios";
import { City, Country, Location } from "../interfaces/country.interface";

const API_MAX_LIMIT = 10000;

interface PagedLocation {
  locations: Location[] | Country[];
  total: number
}

interface PagedLocationGroup {
  group: {id, name, description, locations},
  total: number
}

const removeNullProperties = (params) => {
  return !params ? null :  Object.entries(params).reduce((acc, [key, value]) => {
    if (value != null && value != '') {
      acc[key] = value;
    }
    return acc;
  }, {});
};

class LocationsApi {

  getCountries(): Promise<PagedLocation> {
    const apiUrl = `/api/v1/locations?limit=${API_MAX_LIMIT}`;

    return axios.get(apiUrl)
    .then(response => {
      const countries = response.data.data.map(locationData => {
        const countryData = locationData.data.attributes;
        const citiesData = countryData.children.map(childrenData => this.mapCity(childrenData.data.attributes));
        
        const allCities = [...citiesData];
        const citiesWithImages = allCities.filter(x => x.cover != null);
        const topCities = citiesWithImages.splice(0,4);
        const highlights = citiesWithImages.splice(0,5);
        
        const country:Country = {
          id: countryData.id,
          name: countryData.name,
          cover: countryData.cover,
          tourCount: countryData.itineraries,
          cities: allCities,
          topCities: topCities,
          highlights: highlights,
          tags: allCities.filter(city => !topCities.includes(city) && !highlights.includes(city)),
        }

        return country;
      })
      return Promise.resolve({locations: countries, total: response.data.meta.total_count});
    });
  }

  getLocations(params?): Promise<PagedLocation> {
    const apiUrl = "/api/v1/locations";

    return axios.get(apiUrl, {
      params: params
    })
    .then(response => {
      const locations = response.data.data.map(locationData => {
        const countryData = locationData.data.attributes;
        const citiesData = countryData.children.map(childrenData => this.mapCity(childrenData.data.attributes));        
        const allCities = [...citiesData];
        
        const location:Location = {
          id: countryData.id,
          name: countryData.name,
          cover: countryData.cover,
          tourCount: countryData.itineraries,
          children: allCities,
        }

        return location;
      })
      return Promise.resolve({locations, total: response.data.meta.total_count});
    });
  }

  getLocation(locationId:number): Promise<Location> {
    const apiUrl = `/api/v1/locations/${locationId}`;


    return axios.get(apiUrl)
    .then(response => {
      const locationData = response.data.data.attributes;
      const location: Location = {
        id: locationData.id,
        cover: locationData.cover,
        name: locationData.name,
        description: locationData.description,
        ancestry: locationData.ancestors?.map(a => {
          return {
            id: a.id,
            name: a.name
          }
        }),
        children: locationData.children?.map(sublocation => this.mapCity(sublocation.data.attributes))
      }
      return Promise.resolve(location);
    });
  }

  getLocationGroup(code: string, otherParams?): Promise<PagedLocationGroup> {
    const apiUrl = `/api/v1/location_groups/${code}`;


    return axios.get(apiUrl, {
      params: removeNullProperties(otherParams)
    })
    .then(response => {
      const groupData = response.data.data.attributes;
      const group = {
        id: groupData.id,
        name: groupData.name,
        description: groupData.description,
        locations: groupData.locations?.map(sublocation => this.mapCity(sublocation.attributes))
      }
      return Promise.resolve({group, total: response.data.meta.total_count });
    });
  }

  private mapCity(cityData): City{
    const city:City = {
      id: cityData.id,
      name: cityData.name,
      cover: cityData.cover,
      description: cityData.description,
      tourCount: cityData.itineraries
    };
    return city;
  }

}

export default LocationsApi;